import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMask } from '@react-input/mask';
import { mergeRefs } from 'react-merge-refs';
import { documentNumberMaskOptions, documentSerialMaskOptions, innMaskOptions, phoneMaskOptions } from 'shared/lib';
import { DEFAULT_VALUES } from '../constants';
import { FormValues } from '../types';
import { formSchema } from '../models';
import { Checkbox } from './checkbox';
import { InputField } from './input-field';
import { FormFooter } from './form-footer';
import { AgreementDrawer } from './agreement-drawer';
import styles from './form.module.scss';

export const Form = () => {
  const {
    register,
    setValue,
    handleSubmit,
    watch,
    formState: { errors, isValid, touchedFields },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: DEFAULT_VALUES,
    mode: 'onTouched',
    // values: storedValues,
  });

  const [isAgreementOpen, setIsAgreementOpen] = useState(false);
  const toggleAgreemenOpen = () => setIsAgreementOpen((state) => !state);

  const documentSerialMaskRef = useMask(documentSerialMaskOptions);
  const documentNumberMaskRef = useMask(documentNumberMaskOptions);
  const innMaskRef = useMask(innMaskOptions);
  const phoneMaskRef = useMask(phoneMaskOptions);

  const { ref: documentSerialInputRef, ...documentSerialControl } = register('documentSerial');
  const { ref: documentNumberInputRef, ...documentNumberControl } = register('documentNumber');
  const { ref: innInputRef, ...innControl } = register('INN');
  const { ref: phoneInputRef, ...phoneControl } = register('phoneMobile');

  const middleNameDoesntExist = watch('middleNameField.doesntExist');
  const sameAddress = watch('addressFactField.same');
  const addressReg = watch('addressReg');

  const handleSwitchSameAddress: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const { checked } = e.currentTarget;
    if (checked) {
      setValue('addressFactField.addressFact', addressReg);
    }
  };

  const handleSave = () => null;

  const hasTouchedFields = Object.keys(touchedFields).length > 0;

  const [today] = new Date().toISOString().split('T');
  const minBirthDate = '1900-01-01';

  return (
    <>
      <div className={styles.container}>
        <form className={styles.form} onSubmit={handleSubmit(handleSave)} noValidate>
          <div className={styles.header}>
            <div className={styles.title}>Анкета соискателя</div>
          </div>
          <div className={styles.inputs}>
            <InputField
              className={styles.dynamicInput}
              tabIndex={1}
              label="Имя"
              placeholder="Константин"
              {...register('firstName')}
              invalid={Boolean(errors.firstName)}
            />
            <InputField
              tabIndex={2}
              label="Фамилия"
              placeholder="Константинопольский"
              {...register('lastName')}
              invalid={Boolean(errors.lastName)}
            />
            <InputField
              tabIndex={3}
              label="Отчество"
              disabled={middleNameDoesntExist}
              placeholder={middleNameDoesntExist ? '' : 'Владимирович'}
              {...register('middleNameField.middleName')}
              invalid={Boolean(errors.middleNameField?.middleName)}
            >
              <Checkbox
                label="Нет отчества"
                tabIndex={4}
                id="checkbox-middleName"
                {...register('middleNameField.doesntExist')}
              />
            </InputField>
            <InputField
              className={styles.dynamicInput}
              tabIndex={5}
              type="date"
              min={minBirthDate}
              max={today}
              label="Дата рождения"
              placeholder="10.10.1990"
              {...register('birthDate')}
              invalid={Boolean(errors.birthDate)}
            />
            <InputField
              tabIndex={6}
              label="Место рождения"
              placeholder="г. Смоленск"
              {...register('birthPlace')}
              invalid={Boolean(errors.birthPlace)}
            />
            <InputField
              className={styles.dynamicInput}
              tabIndex={7}
              disabled
              placeholder="Паспорт"
              label="Документ удостоверящий личность"
              invalid={false}
              {...register('documentType')}
            />
            <InputField
              className={styles.dynamicInput}
              ref={mergeRefs([documentSerialInputRef, documentSerialMaskRef])}
              tabIndex={8}
              label="Серия"
              placeholder="0000"
              {...documentSerialControl}
              invalid={Boolean(errors.documentSerial)}
            />
            <InputField
              className={styles.dynamicInput}
              tabIndex={9}
              label="Номер"
              placeholder="111 222"
              ref={mergeRefs([documentNumberInputRef, documentNumberMaskRef])}
              {...documentNumberControl}
              invalid={Boolean(errors.documentNumber)}
            />
            <InputField
              tabIndex={10}
              label="Кем выдан"
              placeholder="ОУФМС РОССИИ в Смоленской области"
              {...register('documentIssuedBy')}
              invalid={Boolean(errors.documentIssuedBy)}
            />
            <InputField
              className={styles.dynamicInput}
              tabIndex={11}
              type="date"
              label="Когда выдан"
              placeholder="11.11.2004"
              min="1997-01-01"
              max={today}
              {...register('documentIssueDate')}
              invalid={Boolean(errors.documentIssueDate)}
            />
            <InputField
              tabIndex={12}
              label="Адрес постоянной регистрации"
              placeholder="г. Москва, ул. Тверская, д. 1, кв. 1"
              {...register('addressReg')}
              invalid={Boolean(errors.addressReg)}
            />
            <InputField
              tabIndex={13}
              label="Адрес фактического места проживания"
              placeholder="г. Москва, ул. Тверская, д. 1, кв. 1"
              disabled={sameAddress}
              {...register('addressFactField.addressFact')}
              invalid={Boolean(errors.addressFactField?.addressFact)}
            >
              <Checkbox
                label="Совпадает с адресом регистрации"
                tabIndex={14}
                id="checkbox-addressFact"
                {...register('addressFactField.same')}
                onChange={handleSwitchSameAddress}
              />
            </InputField>
            <InputField
              className={styles.dynamicInput}
              ref={mergeRefs([innInputRef, innMaskRef])}
              tabIndex={15}
              label="ИНН"
              placeholder="11244803058"
              {...innControl}
              invalid={Boolean(errors.INN)}
            />
            <InputField
              className={styles.dynamicInput}
              ref={mergeRefs([phoneInputRef, phoneMaskRef])}
              tabIndex={16}
              label="Мобильный телефон"
              placeholder="+7 000 000 00 00"
              {...phoneControl}
              invalid={Boolean(errors.phoneMobile)}
            />
          </div>
          <FormFooter
            error={hasTouchedFields || (hasTouchedFields && !isValid)}
            register={register}
            toggleAgreementDrawer={toggleAgreemenOpen}
          />
        </form>
      </div>
      <AgreementDrawer open={isAgreementOpen} onClose={toggleAgreemenOpen} />
    </>
  );
};
